import { useEffect, useRef, useState } from 'react'
import { t } from '@/lib/i18n'

/** 录音上限。稿子的 MAX_REC —— 「给爸妈听一句」不需要更长。 */
export const MAX_SECONDS = 60

export type RecorderState = {
  /** 这台设备 + 这个页面能不能录。false 时 UI 要**说明原因**，不是默默藏起来。 */
  available: boolean
  /** 不能录 / 出错时的那句话。available 为 false 时一定有值。 */
  why: string | null
  recording: boolean
  /** 已录秒数，用来显示「停止 0:12」。 */
  seconds: number
  start: () => void
  stop: () => void
}

/**
 * 稿子 ATT 模块里录音那一段（`canRec` / `startRec` / `tick` / `killStream`）。
 *
 * 为什么可用性判断这么啰嗦：`getUserMedia` 只在**安全上下文**可用 ——
 * localhost 和 https 行，而**手机通过 `http://192.168.x.x` 打开局域网调试时不行**。
 * 那是这个项目最常见的真机测试方式，所以「不能录」是一个正常状态，不是异常。
 * 稿子为此专门有一个 `#attWhy`：不能录的时候把原因说出来，并指向「选一个音频文件」。
 */
export function useRecorder(onDone: (file: File) => void): RecorderState {
  const supported =
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== 'undefined'

  const [why, setWhy] = useState<string | null>(supported ? null : t('homework.rec_why_insecure'))
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)

  const rec = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const chunks = useRef<Blob[]>([])
  const timer = useRef<number | null>(null)

  // 松开麦克风。不 stop track 的话手机状态栏那个录音红点会一直亮着 ——
  // 用户会以为我们在偷听。
  const release = () => {
    stream.current?.getTracks().forEach((track) => track.stop())
    stream.current = null
    rec.current = null
    if (timer.current !== null) {
      window.clearInterval(timer.current)
      timer.current = null
    }
  }

  // 组件卸载（sheet 关掉、换一条作业）时也要松开
  useEffect(() => release, [])

  const stop = () => {
    if (rec.current?.state === 'recording') rec.current.stop()
  }

  const start = async () => {
    setWhy(null)
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.current = media

      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(media)
      } catch {
        release()
        setWhy(t('homework.rec_why_unsupported'))
        return
      }

      chunks.current = []
      rec.current = recorder
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunks.current.push(e.data)
      }
      recorder.onstop = () => {
        // mimeType 用录制器自己报的：Safari 给 audio/mp4，Chrome 给 audio/webm。
        // 写死一个会让服务端那道白名单在另一半浏览器上拒收。
        const type = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunks.current, { type })
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'
        release()
        setRecording(false)
        setSeconds(0)
        if (blob.size > 0) onDone(new File([ blob ], `recording.${ext}`, { type }))
      }

      const startedAt = Date.now()
      recorder.start()
      setRecording(true)
      setSeconds(0)
      timer.current = window.setInterval(() => {
        const elapsed = Math.round((Date.now() - startedAt) / 1000)
        setSeconds(elapsed)
        // 到上限自己停下并**说出来**，不是悄悄截断
        if (elapsed >= MAX_SECONDS) {
          stop()
          setWhy(t('homework.rec_why_capped', { count: MAX_SECONDS }))
        }
      }, 250)
    } catch (err) {
      release()
      const name = (err as DOMException)?.name ?? ''
      setWhy(
        name === 'NotAllowedError'
          ? t('homework.rec_why_denied')
          : t('homework.rec_why_failed', { reason: name || t('homework.rec_why_unknown') }),
      )
    }
  }

  return { available: supported, why, recording, seconds, start, stop }
}

/** 0:07 / 1:03。秒数超过 59 也要读得懂。 */
export function formatSeconds(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
